import React, { useCallback, useEffect, useState } from 'react';
import {
	Button,
	ExtendedTheme,
	Heading,
	TextBlock,
	Loader,
	UserInterfaceCheck
} from 'neon-design-system';
import { makeStyles, createStyles } from '@material-ui/styles';
import { useHistory, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Redirect } from 'react-router-dom'
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { useLogger } from '../../providers/logger/Logger';
import { useRoutesHistory } from '../../providers/RoutesHistoryProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	registerInterestMessage: {
		textAlign: 'center',
		alignItems: 'center',
		justifyContent: 'center',
		marginLeft: 'auto',
		marginRight: 'auto',
		maxWidth: 700
	},
	content: {
		maxWidth: 700,
		margin: '0 auto',
		'& .promotional-action-button': {
			display: 'inline-block',
			cursor: 'pointer',
			color: `${theme.palette.common.white} !important`,
			backgroundColor: `${theme.componentColors.button.primary.backgroundColor.main} !important`,
			padding: theme.spacing(1.5, 3, 1.5, 3),
			fontSize: theme.typography.body1.fontSize,
			letterSpacing: '0.02em',
			textTransform: 'none',
			boxShadow: 'none',
			lineHeight: theme.typography.body1.lineHeight,
			borderRadius: '3px',
			fontWeight: 600,
			borderWidth: 0,
			outline: 'none !important',
			transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
			'&:hover, &:active': {
				backgroundColor: `${theme.componentColors.button.primary.backgroundColor.hover} !important`
			},
			'& span': {
				fontSize: `${theme.typography.body1.fontSize} !important`,
				color: `${theme.palette.common.white} !important`,
				backgroundColor: 'transparent !important',
				lineHeight: `${theme.typography.body1.lineHeight} !important`,
				letterSpacing: '0.02em !important'
			},
			'& em': {
				fontStyle: 'normal !important'
			},
			'& u': {
				textDecoration: 'none !important'
			},
			'& strong': {
				fontWeight: '600 !important'
			}
		},
		'& a': {
			color: theme.palette.secondary.main,
			textDecoration: 'none',
			'&:hover, &:focus': {
				textDecoration: 'none'
			},
			'& > u': {
				textDecoration: 'none'
			}
		},
		'& h1': {
			...theme.customTypography.title1.medium,
			[theme.breakpoints.up('md')]: {
				...theme.customTypography.title1.medium
			},
			[theme.breakpoints.up('xl')]: {
				...theme.customTypography.title1.large
			}
		},
		'& h2': {
			...theme.customTypography.headline1.medium,
			[theme.breakpoints.up('md')]: {
				...theme.customTypography.headline1.medium
			},
			[theme.breakpoints.up('xl')]: {
				...theme.customTypography.headline1.large
			}
		},
		'& h3': {
			...theme.customTypography.headline2.medium,
			[theme.breakpoints.up('md')]: {
				...theme.customTypography.headline2.medium
			},
			[theme.breakpoints.up('xl')]: {
				...theme.customTypography.headline2.large
			}
		},
		'& p, & li': {
			...theme.typography.body1,
			lineHeight: '1.4'
		},
		'& .fr-img-caption .fr-img-wrap > span': theme.typography.caption,
		'& .fr-img-space-wrap':  theme.typography.body1,
		'& img': {
			borderRadius: 4,
			height: 'auto'
		}
	},
	icon: {
		display: 'flex',
		margin: `${theme.spacing(8)}px auto 0`,
		border: `4px solid ${theme.themeColors.success.main}`,
		borderRadius: '50%',
		width: 64,
		height: 64,
		justifyContent: 'center',
		alignItems: 'center'
	},
	header: {
		marginTop: theme.spacing(6)
	},
	subHeader: {
		marginTop: theme.spacing(1),
		textAlign: 'center'
	},
	subHeaderSuccess: {
		marginTop: theme.spacing(1.25),
		textAlign: 'center'
	},
	callToActionButtonSuccess: {
		marginTop: theme.spacing(4)
	}
}));

export const PROMOTIONAL_CONTENT = gql`
	query promotionalContent($id: ID) {
		document: findPromotionalContentById(id: $id) {
			_id
			promotionPage {
				name
				html
				successTitle
				successMessage
			}
		}
	}
`;

export const NOTIFY_REGISTER_INTEREST_IN_PROMOTIONAL_CONTENT = gql`
mutation notifyRegisteredInterestInPromotionalContent($item: NotifyRegisteredInterestInPromotionalContentInput!) {
		notifyRegisteredInterestInPromotionalContent(item: $item)
	}
`;


const PromotionPage: React.FC = () => {
	const classes = useStyles();
	const { _id } = useParams<{ _id: string }>();
	const history = useHistory();
	const routesHistory = useRoutesHistory();
	const { printLog } = useLogger()

	const [ success, setSuccess ] = useState(false);

	const { loading, data } = useQuery(PROMOTIONAL_CONTENT, { variables: { id: _id }, fetchPolicy: 'network-only' })
	const [ notifyRegisteredInterestInPromotionalContent ] = useMutation(NOTIFY_REGISTER_INTEREST_IN_PROMOTIONAL_CONTENT);

	const promotionPage = data?.document?.promotionPage;

	const registerInterestHandler = useCallback(async () => {
		await notifyRegisteredInterestInPromotionalContent({
			variables: {
				item: {
					promotionalContentId: _id
				}
			}
		});
		printLog(
			LogLevel.INFO,
			LogAction.RegisteredInterest,
			'User registered interest',
			{
				promotionalContentId: _id,
				interest: promotionPage.name
			}
		);
		setSuccess(true);
	}, [notifyRegisteredInterestInPromotionalContent, printLog, promotionPage, _id]);

	useEffect(() => {
		const actionButtons = document.getElementById('promotion-html')?.querySelectorAll('.promotional-action-button');
		if (actionButtons) {
			actionButtons.forEach((button) => {
				button.addEventListener('click', registerInterestHandler);
			});

			return () => actionButtons.forEach((button) => {
				button.removeEventListener('click', registerInterestHandler, false)
			})
		}
	}, [registerInterestHandler]);

	const redirectToPrevPageHandler = () => {
		if (routesHistory && routesHistory.history.length) {
			history.goBack()
		} else {
			history.push(baseUrls.forYou);
		}
	}

	if (loading) return <Loader/>;

	if (!promotionPage) return <Redirect to={baseUrls.forYou} />

	if(!success) {
		return (
			<div className={classes.content}>
				<div
					id="promotion-html"
					className="fr-view"
					dangerouslySetInnerHTML={{ __html: promotionPage.html ?? '' }}
				/>
			</div>
		);
	}

	return (
		<div id="demo-page" className={classes.registerInterestMessage}>
			<div className={classes.icon}>
				<UserInterfaceCheck color="success" size="large"/>
			</div>
			<Heading className={classes.header} xs="small" sm="medium" xl="large" styleName="largeTitle">
				{promotionPage.successTitle ? promotionPage.successTitle : <>We will be in touch</>}
			</Heading>
			<TextBlock className={classes.subHeaderSuccess} xs="small" sm="medium" xl="large" styleName="largeBody2" ink="muted">
				{promotionPage.successMessage ? promotionPage.successMessage : <>A member of the desk will contact you to set up your access to {promotionPage.name}.</>}
			</TextBlock>
			<Button
				id="demo-page-redirect-button"
				className={classes.callToActionButtonSuccess}
				colorType="success"
				size="large"
				onClick={redirectToPrevPageHandler}
			>
					Continue
			</Button>
		</div>
	);
}

export default PromotionPage;
